"use client";

import { useEffect } from "react";
import { figure, panel, script } from "./fonts";
// global-error.tsx REPLACES the root layout when it fires, so none of that
// layout's <link> tags — this stylesheet included — ship with it. Import it
// directly or the page renders completely unstyled.
import "./globals.css";

/**
 * The root-layout error boundary. It fires when `app/layout.tsx` itself throws,
 * or when a throw has nowhere else to land, and it replaces that layout
 * entirely: its own <html>, its own <body>, its own fonts, its own viewport.
 *
 * THEMED PER APP, DELIBERATELY. The canonical flare copy hardcodes a cream
 * #FAF4E9 page and Fraunces — another app's face, arriving here only because
 * this file gets copied around. Neither survives here: this is a purple
 * ground behind a cream panel with a tomato crown, in Menagerie's own three
 * faces. `flare/scripts/check-reporters.mjs` gates this one file by MARKER
 * rather than by diff for exactly this reason, so the theming survives a
 * re-copy. Do not restore it to canonical.
 *
 * Task 14 landed the reporter call — the `useEffect` below, taken from
 * `flare/reporters/next/app/global-error.tsx` and ADDED to this component
 * rather than replacing it. It is the third marker
 * `flare/scripts/check-reporters.mjs` looks for, alongside "use client" and the
 * exported `GlobalError`. The import is dynamic for the reason the canonical
 * copy is: this file must render even if the module graph is what broke.
 *
 * It landed together with the sentence in the copy below that says the error
 * was reported, because an error page that claims telemetry it does not have is
 * worse than one that says nothing — and, now, one that has telemetry and does
 * not say so leaves the reader wondering whether to write in. Both halves move
 * together or neither does. `lib/flare.ts` no-ops off production
 * (`flareEnabled()` requires `VERCEL_ENV === "production"`), so the sentence is
 * true where a reader can see it and the effect is silent everywhere else.
 *
 * The three faces are imported from `./fonts` on purpose. Hanging the root
 * layout's `.variable` classNames on this page's own <html> is the only way the CSS
 * custom properties in globals.css resolve to real webfonts; without it every
 * `var(--font-panel)` silently falls through to its system fallback on the one
 * screen that renders when everything else has failed.
 */

// Literal hex, not var(), on every element below. If the stylesheet import is
// itself what failed, the palette still lands and the page is still Menagerie.
const CREAM = "#F5EFD5";
const KEYLINE = "#111014";
const TOMATO = "#E8412E";
const PURPLE = "#6B2D8C";
const SUN = "#F5C518";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${script.variable} ${panel.variable} ${figure.variable}`}>
      <head>
        {/* The global-error path bypasses `metadata`/`viewport` resolution
            entirely, so without this an installed PWA renders at desktop
            width. */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light" />
        <title>Menagerie stopped working</title>
      </head>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "1.5rem",
          background: PURPLE,
          color: KEYLINE,
          font: "400 1rem/1.55 ui-sans-serif, system-ui, -apple-system, sans-serif",
        }}
      >
        <main
          style={{
            width: "100%",
            maxWidth: "32rem",
            border: `5px solid ${KEYLINE}`,
            background: CREAM,
          }}
        >
          {/* The crown, in miniature. Cream on tomato is 3.48:1 — display
              lettering, above the 3:1 floor — with the hard black shadow the
              direction contract asks for. */}
          <div
            style={{
              padding: "0.75rem 1rem",
              background: TOMATO,
              borderBottom: `5px solid ${KEYLINE}`,
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-script), cursive",
                fontSize: "clamp(2rem, 8vw, 3rem)",
                lineHeight: 1,
                color: CREAM,
                textShadow: `3px 3px 0 ${KEYLINE}`,
              }}
            >
              Menagerie
            </p>
          </div>

          {/* Keyline on sun: 11.63:1. */}
          <h1
            style={{
              margin: 0,
              padding: "0.75rem 1rem",
              background: SUN,
              borderBottom: `5px solid ${KEYLINE}`,
              fontFamily: "var(--font-panel), Georgia, serif",
              fontWeight: 400,
              fontSize: "clamp(1.125rem, 4vw, 1.5rem)",
              lineHeight: 1.25,
              color: KEYLINE,
            }}
          >
            Menagerie stopped working
          </h1>

          <div style={{ padding: "1.25rem 1rem 1.5rem" }}>
            {/* Keyline on cream: 16.42:1. */}
            <p style={{ margin: "0 0 1.25rem", color: KEYLINE, maxWidth: "65ch" }}>
              Nothing in the toy box was changed, and trying again usually
              works. The fault was reported.
            </p>
            <button
              type="button"
              onClick={reset}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "44px",
                padding: "0 1.25rem",
                border: `3px solid ${KEYLINE}`,
                background: SUN,
                color: KEYLINE,
                fontFamily: "var(--font-panel), Georgia, serif",
                fontSize: "0.9375rem",
                letterSpacing: "0.01em",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            {error.digest ? (
              <p
                style={{
                  margin: "1rem 0 0",
                  fontFamily: "var(--font-figure), ui-monospace, monospace",
                  fontSize: "0.75rem",
                  color: PURPLE,
                }}
              >
                {error.digest}
              </p>
            ) : null}
          </div>
        </main>
      </body>
    </html>
  );
}
